import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { SoundToggle } from "@/components/ui/SoundToggle";
import { OPEN_SEARCH_EVENT } from "@/components/search/GlobalSearch";
import { logout, useAuthUser } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { nameInitial } from "@/lib/names";
import { useIsAdminPortal } from "@/lib/portal";
import { NotificationsBell } from "./NotificationsBell";

function resolveCrumbLabel(
  segment: string,
  parent: string | undefined,
  t: TFunction,
  root?: string,
): string | undefined {
  if (segment === "new") {
    if (parent === "sessions") return t("sessions.createTitle");
    if (parent === "reviewers") return t("reviewers.createTitle");
    if (parent === "doctors") return t("doctors.createTitle");
  }
  if (segment === "edit") return t("common.edit");
  if (/^\d+$/.test(segment)) {
    if (root === "admin") {
      if (parent === "centrals") return t("adminCentralDetail.title");
      if (parent === "doctors") return t("adminDoctorDetail.title");
      if (parent === "reviewers") return t("adminReviewerDetail.title");
      if (parent === "sessions") return t("adminSessionDetail.title");
      if (parent === "countries") return t("adminCountries.detailTitle");
    }
    if (parent === "doctors") return t("doctors.title");
    if (parent === "sessions") return t("sessions.detailTitle");
    if (parent === "reviewers") return t("reviewers.title");
  }
  if (parent === "reports" && segment !== "schedules" && segment !== "new") {
    return t(`adminReports.types.${segment}.title`, { defaultValue: segment });
  }
  const sectionLabels: Record<string, string> = {
    admin: t("nav.admin"),
    centrals: t("nav.centrals"),
    doctors: t("nav.doctors"),
    sessions: t("nav.sessions"),
    reviewers: t("nav.reviewers"),
    contracts: t("nav.contracts"),
    moderators: t("nav.moderators"),
    "subscription-requests": t("nav.subscriptionRequests"),
    packages: t("nav.packages"),
    subscriptions: t("nav.subscriptions"),
    specializations: t("nav.specializations"),
    "pending-field-changes": t("nav.pendingFieldChanges"),
    audit: t("nav.audit"),
    reports: t("nav.reports"),
    schedules: t("adminReports.schedulesTitle"),
    exports: t("nav.exports"),
    completeness: t("adminCompleteness.title"),
    subscription: t("nav.subscription"),
    checkout: t("subscription.checkoutTitle"),
    manage: t("subscription.manageTitle"),
    renew: t("subscription.renewTitle"),
    result: t("subscription.resultTitle"),
    "api-keys": t("nav.apiKeys"),
    "api-docs": t("nav.apiDocs"),
    admins: t("nav.admins"),
    settings: t("nav.settings"),
    activities: t("nav.activities"),
    "chronic-disease-consent": t("nav.chronicDiseaseConsent"),
    notifications: t("nav.notifications"),
  };
  return sectionLabels[segment];
}

function breadcrumbFor(
  pathname: string,
  titles: Record<string, string>,
  home: string,
  t: TFunction,
): { label: string; to?: string }[] {
  if (pathname === "/") return [{ label: titles["/"] }];
  const parts = pathname.split("/").filter(Boolean);
  const root = parts[0];
  const crumbs: { label: string; to?: string }[] = [{ label: home, to: "/" }];
  let acc = "";
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const parent = i > 0 ? parts[i - 1] : undefined;
    acc += `/${part}`;
    crumbs.push({
      label: titles[acc] ?? resolveCrumbLabel(part, parent, t, root) ?? part,
      to: acc,
    });
  }
  crumbs[crumbs.length - 1].to = undefined;
  return crumbs;
}

function pageTitleFor(
  pathname: string,
  titles: Record<string, string>,
): string {
  if (titles[pathname]) return titles[pathname];
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return titles["/"] ?? "";
  const base = `/${parts[0]}`;
  return titles[base] ?? titles["/"] ?? "";
}

interface TopbarProps {
  onMenuClick: () => void;
}

export function Topbar({ onMenuClick }: TopbarProps) {
  const adminPortal = useIsAdminPortal();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAuthUser();
  const [scrolled, setScrolled] = useState(false);

  const titles: Record<string, string> = {
    "/": t("nav.dashboard"),
    "/doctors/active": t("nav.doctorsActive"),
    "/doctors/inactive": t("nav.doctorsInactive"),
    "/doctors/new": t("doctors.createTitle"),
    "/sessions": t("nav.sessions"),
    "/sessions/new": t("sessions.createTitle"),
    "/reviewers": t("nav.reviewers"),
    "/reviewers/new": t("reviewers.createTitle"),
    "/contracts": t("nav.contracts"),
    "/subscription": t("nav.subscription"),
    "/subscription/checkout": t("subscription.checkoutTitle"),
    "/subscription/manage": t("subscription.manageTitle"),
    "/subscription/renew": t("subscription.renewTitle"),
    "/subscription/result": t("subscription.resultTitle"),
    "/api-keys": t("nav.apiKeys"),
    "/api-docs": t("nav.apiDocs"),
    "/admins": t("nav.admins"),
    "/settings": t("nav.settings"),
    "/settings/notifications": t("nav.notificationPreferences"),
    "/activities": t("nav.activities"),
    "/admin": t("nav.admin"),
    "/admin/centrals": t("nav.centrals"),
    "/admin/doctors": t("nav.doctors"),
    "/admin/reviewers": t("nav.reviewers"),
    "/admin/sessions": t("nav.sessions"),
    "/admin/moderators": t("nav.moderators"),
    "/admin/subscription-requests": t("nav.subscriptionRequests"),
    "/admin/packages": t("nav.packages"),
    "/admin/subscriptions": t("nav.subscriptions"),
    "/admin/specializations": t("nav.specializations"),
    "/admin/pending-field-changes": t("nav.pendingFieldChanges"),
    "/admin/audit": t("nav.audit"),
    "/admin/reports": t("nav.reports"),
    "/admin/reports/schedules": t("adminReports.schedulesTitle"),
    "/admin/exports": t("nav.exports"),
    "/admin/doctors/completeness": t("adminCompleteness.title"),
  };

  const crumbs = breadcrumbFor(pathname, titles, t("nav.home"), t);
  const pageTitle = pageTitleFor(pathname, titles);
  const showCentralTitle = !adminPortal;
  const showBreadcrumbs =
    adminPortal || pathname.split("/").filter(Boolean).length > 1;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="sticky top-0 z-50 bg-[linear-gradient(to_bottom,rgb(var(--color-canvas))_55%,rgb(var(--color-canvas)/0)_100%)] px-3 pb-2 pt-3.5 sm:px-5 lg:px-5">
      <header
        className={cn(
          "flex h-topbar items-center gap-3 rounded-card px-4 text-ink-1 sm:gap-4 sm:px-5",
          !adminPortal && "glass-topbar",
          !adminPortal && scrolled && "glass-topbar-scrolled",
          adminPortal &&
            "border border-line bg-card/95 backdrop-blur [background-color:rgb(var(--color-card)/0.95)]",
        )}
      >
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={t("nav.openMenu")}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line/20 text-ink-2 hover:bg-canvas hover:scale-105 transition-all lg:hidden"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>

        {showCentralTitle && !showBreadcrumbs ? (
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] font-black text-ink-1">
              {user ? t("topbar.welcome", { name: user.name }) : pageTitle}
            </div>
            {user?.central_name && (
              <div className="truncate text-[11px] text-ink-3">
                {user.central_name}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="min-w-0 flex-1 truncate text-[14px] font-bold text-ink-1 sm:hidden">
              {pageTitle}
            </div>
            <nav
              aria-label={t("nav.breadcrumb")}
              className="hidden min-w-0 flex-1 items-center gap-1.5 text-[12.5px] sm:flex"
            >
              {crumbs.map((c, i) => (
                <span key={i} className="flex min-w-0 items-center gap-1.5">
                  {i > 0 && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="shrink-0 text-ink-3/40 rtl:-scale-x-100"
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  )}
                  {c.to ? (
                    <Link
                      to={c.to}
                      className="truncate text-ink-3 hover:text-ink-1"
                    >
                      {c.label}
                    </Link>
                  ) : (
                    <span className="truncate font-semibold text-ink-1">
                      {c.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          </>
        )}

        <div className="hidden h-6 w-px bg-line/15 sm:block" />
        <div
          className="flex flex-1 items-center justify-end gap-2 sm:flex-none"
          dir="ltr"
        >
          {user && showBreadcrumbs && (
            <span className="hidden max-w-[160px] truncate text-[12px] font-semibold text-ink-2 md:inline">
              {user.name}
            </span>
          )}
          {/* <button
            type="button"
            onClick={() => window.dispatchEvent(new Event(OPEN_SEARCH_EVENT))}
            aria-label={t("globalSearch.title")}
            title={`${t("globalSearch.title")} (Ctrl+K)`}
            data-testid="topbar-search"
            className="grid h-9 w-9 place-items-center rounded-sm border border-line/20 text-ink-2 hover:bg-canvas"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </button> */}
          <ThemeToggle />
          {/* <SoundToggle /> */}
          <LanguageSwitcher />
          <button
            type="button"
            onClick={handleLogout}
            aria-label={t("common.logout")}
            title={t("common.logout")}
            className="grid h-9 w-9 place-items-center rounded-lg border border-line/20 text-ink-2 hover:border-rose/30 hover:text-rose hover:bg-rose/5 hover:scale-105 transition-all"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
          </button>
          {showCentralTitle && !showBreadcrumbs && user && (
            <div
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border-2 border-primary/30 bg-gradient-to-br from-primary to-brand-teal text-xs font-bold text-white shadow-[0_0_12px_rgba(23,75,181,0.3)]"
              title={user.name}
            >
              {nameInitial(user.name)}
            </div>
          )}
        </div>
      </header>
    </div>
  );
}
